import React, { useState, useEffect } from 'react';
import { usersAPI } from '../../utils/api';
import { Shield, Check, X, ShieldCheck, Info } from 'lucide-react';
import toast from 'react-hot-toast';

const PERMISSIONS = [
  { id: 'dashboard', label: 'Dashboard', desc: 'Main overview and stats' },
  { id: 'assets', label: 'Asset Management', desc: 'Manage assets and QR codes' },
  { id: 'jobs', label: 'Job Management', desc: 'Create and track field jobs' },
  { id: 'workflows', label: 'Workflows', desc: 'Design inspection templates' },
  { id: 'tech_view', label: 'Technician View', desc: 'Mobile-first job execution' },
  { id: 'users', label: 'User Management', desc: 'Manage team and roles' },
  { id: 'taxonomy', label: 'Asset Taxonomy', desc: 'Equipment classification' },
  { id: 'sops', label: 'Standard Procedures', desc: 'Attach SOPs to assets' },
  { id: 'analytics', label: 'Analytics & Reporting', desc: 'Executive dash and insights' },
];

const ROLES = ['admin', 'supervisor', 'technician'];

export default function PermissionMatrix() {
  const [matrix, setMatrix] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    usersAPI.getPermissionsMatrix().then(r => setMatrix(r.data.permissions)).finally(() => setLoading(false));
  }, []);

  const hasPermission = (role, perm) => matrix.some(m => m.role === role && m.permission === perm);

  const toggle = async (role, permission) => {
    if (role === 'admin') return toast.error('Admin permissions are locked for safety');
    
    const enabled = !hasPermission(role, permission);
    try {
      await usersAPI.togglePermission({ role, permission, enabled });
      setMatrix(prev => enabled 
        ? [...prev, { role, permission }] 
        : prev.filter(m => !(m.role === role && m.permission === permission))
      );
      toast.success(`Permission ${enabled ? 'granted' : 'revoked'}`);
    } catch (e) {
      toast.error('Failed to update permission');
    }
  };

  if (loading) return <div className="spinner" style={{ margin: '40px auto' }} />;

  return (
    <div className="card animate-in" style={{ padding: 0, overflow: 'hidden', border: '1px solid var(--border)' }}>
      <div style={{ padding: '28px 32px', borderBottom: '1px solid var(--border)', background: 'linear-gradient(to right, var(--bg-muted)20, transparent)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }}>
          <div style={{ padding: 10, borderRadius: 12, background: 'var(--accent-muted)', color: 'var(--accent)' }}>
            <ShieldCheck size={24} />
          </div>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text)', letterSpacing: '-0.02em' }}>Access Control Matrix</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
              <Info size={14} /> Global permission mapping for system roles
            </div>
          </div>
        </div>
      </div>

      <div className="scroll-x">
        <table className="perm-table">
          <thead>
            <tr>
              <th style={{ position: 'sticky', left: 0, zIndex: 10, background: 'var(--bg-card)', minWidth: 280 }}>System Module</th>
              {ROLES.map(r => (
                <th key={r} className="role-cell" style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 4 }}>ROLE</div>
                  <div style={{ fontSize: 13, color: 'var(--text)' }}>{r.toUpperCase()}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PERMISSIONS.map((p, idx) => (
              <tr key={p.id} className="perm-row">
                <td style={{ position: 'sticky', left: 0, zIndex: 5, background: 'var(--bg-card)' }}>
                  <div className="perm-info">
                    <span className="perm-label">{p.label}</span>
                    <span className="perm-desc">{p.desc}</span>
                  </div>
                </td>
                {ROLES.map(role => {
                  const active = hasPermission(role, p.id);
                  const isLocked = role === 'admin';
                  return (
                    <td key={role} className="role-cell">
                      <label style={{ 
                        cursor: isLocked ? 'not-allowed' : 'pointer', 
                        display: 'flex', alignItems: 'center', justifyContent: 'center' 
                      }}>
                        <input 
                          type="checkbox" 
                          checked={active} 
                          disabled={isLocked}
                          onChange={() => toggle(role, p.id)}
                          className="checkbox-custom"
                        />
                      </label>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ padding: '20px 32px', background: 'var(--bg-muted)30', borderTop: '1px solid var(--border)', fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', fontWeight: 500 }}>
        <Shield size={14} style={{ display:'inline', marginRight:6, verticalAlign:'middle' }} />
        Security Note: Changes are logged and applied to all existing and new accounts for the selected roles.
      </div>
    </div>
  );
}
